/* eslint-disable prefer-const */
import {
  Divider,
  GKButton,
  GKEditor,
  GKTextArea,
  GKTextField,
  Grid2,
  HelpIcon,
  InputAdornment,
  SimpleLoader,
  Typography,
  VerifySolidIcon,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { isEmpty } from "lodash";
import InvestorSettingsConfirmation from "Pages/Settings/InvestorSettingsConfirmation";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { handleFreshReturnFormData } from "utils/formHelpers";
import useDocumentTitle from "utils/useDocumentTitle";
import { emailRegex, validateNotWhitespaceOnly } from "utils/validationSchema";

interface FormValues {
  id?: number;
  domainName?: string;
  paymentMethods?: string;
  paymentStep?: true;
  kycType?: string;
  quotes?: string;
  agreement?: string;
  codePrefix?: string;
  revenueWithGst?: string;
  codeLength?: number;
  companyLogo?: any;
  qrCode?: any;
  companyMetaName?: string;
  tagLine?: string;
  tagLineBody?: string;
  colorPalette?: {
    primary: string;
    secondary: string;
  };
  advisorPhoto?: any;
  advisorName?: string;
  invoiceSeries?: string;
  sebiRegistered?: string;
  yearsOfExperience?: number;
  advisorInfo?: string;
  advisorPhone?: string;
  advisorEmail?: string;
  advisorAddress?: string;
  company?: number;
  isVerified?: boolean;
  quoteOwnerUrl?: any;
  quoteOwnerName?: string;
  quoteOwnerDesignation?: string;
  termsConditions?: string;
  refundPolicy?: string;
  pricingPolicy?: string;
  footerTagLine?: string;
  footerTagLineBody?: string;
  youtubeLink?: string;
  youtubeThumbnail?: any;
  managerDisclosure?: string;
}

const WebsiteSettings = () => {
  useDocumentTitle("Investor Settings");

  const userData = useAppSelector((state) => state?.user?.userData);

  const [investorSettingsConfirmation, setInvestorSettingsConfirmation] =
    useState(false);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const [croppedYoutubeThumbnail, setCroppedYoutubeThumbnail] = useState<
    string | null
  >(null);

  const [croppedAdvisorImage, setCroppedAdvisorImage] = useState<string | null>(
    null,
  );

  const [selectedAdvisorImage, setSelectedAdvisorImage] = useState<
    string | null
  >(null);

  const [selectedYoutubeThumbnail, setSelectedYoutubeThumbnail] = useState<
    string | null
  >(null);

  const [selectedQuoteImage, setSelectedQuoteImage] = useState<string | null>(
    null,
  );
  const [croppedQuoteImage, setCroppedQuoteImage] = useState<string | null>(
    null,
  );

  const { data, refetch, isFetching } = useFetchQuery({
    key: ["GET_INVESTOR_PROFILE"],
    route: `/company/${userData?.companyId}/publish_settings/`,
    success: (response: any) => {
      setCroppedAdvisorImage(response?.data?.advisorPhoto);
      setCroppedYoutubeThumbnail(response?.data?.youtubeThumbnail);
      setCroppedImage(response?.data?.companyLogo);
      setCroppedQuoteImage(response?.data?.quoteOwnerUrl);
    },
  });

  const { mutate: updateInvestorProfile, isLoading: updateInvestorLoading } =
    useMutation({
      mutationKey: ["GET_INVESTOR_PROFILE_DOMAIN"],
      mutationFn: (data: any) =>
        axiosInstance.patch(
          `/company/${userData?.companyId}/publish_settings/`,
          data,
        ),
      onSuccess: () => {
        refetch();
        setInvestorSettingsConfirmation(false);
      },
    });

  const form = useForm<FormValues>({
    defaultValues: {
      domainName: "",
      revenueWithGst: "false",
      paymentMethods: "",
      invoiceSeries: "",
      paymentStep: null,
      kycType: "",
      agreement: "",
      codePrefix: "",
      codeLength: 3,
      companyLogo: null,
      qrCode: null,
      companyMetaName: "",
      tagLine: "",
      tagLineBody: "",
      advisorPhoto: null,
      advisorName: "",
      sebiRegistered: "",
      yearsOfExperience: 0,
      advisorInfo: "",
      advisorPhone: "",
      advisorEmail: "",
      advisorAddress: "",
      quotes: "",
      pricingPolicy: "",
      refundPolicy: "",
      termsConditions: "",
      quoteOwnerUrl: null,
      quoteOwnerName: "",
      youtubeLink: "",
      youtubeThumbnail: null,
      quoteOwnerDesignation: "",
      managerDisclosure: "",
      colorPalette: {
        primary: "#006580",
        secondary: "#63A387",
      },
      isVerified: false,
    },
    values: {
      ...data?.data,
      agreementTemplate: "",
      colorPalette: data?.data?.colorPalette
        ? JSON.parse(data?.data?.colorPalette)
        : {
            primary: "#006580",
            secondary: "#63A387",
          },
    },
  });

  const { mutate: confirmDomainName, isLoading } = useMutation({
    mutationKey: ["GET_INVESTOR_PROFILE_DOMAIN"],
    mutationFn: (data: string) =>
      axiosInstance.get(`/company/check_domain_name/?domain_name=${data}`),
    onSuccess: (response) => {
      const { available } = response.data;
      if (!available) {
        form.setError("domainName", {
          type: "manual",
          message: "Domain is not available. Please choose another one.",
        });
      } else {
        form.clearErrors("domainName");
        form.setValue("isVerified", true);
      }
    },
  });

  if (isFetching) return <SimpleLoader />;

  const handlePhotoDelete = (field: string) => {
    const formData: any = new FormData();
    formData.append(field, "");
    updateInvestorProfile(formData);
  };

  return (
    <form
      onSubmit={form.handleSubmit((values: FormValues) => {
        let newObject = { ...values };
        if (
          isEmpty(newObject?.companyLogo) ||
          typeof newObject?.companyLogo === "string"
        ) {
          delete newObject.companyLogo;
        } else {
          newObject = { ...newObject, companyLogo: values.companyLogo[0] };
        }
        if (
          isEmpty(newObject?.youtubeThumbnail) ||
          typeof newObject?.youtubeThumbnail === "string"
        ) {
          delete newObject.youtubeThumbnail;
        } else {
          newObject = {
            ...newObject,
            youtubeThumbnail: values.youtubeThumbnail[0],
          };
        }
        if (
          isEmpty(newObject?.qrCode) ||
          typeof newObject?.qrCode === "string"
        ) {
          delete newObject.qrCode;
        } else {
          newObject = { ...newObject, qrCode: values.qrCode[0] };
        }
        if (
          isEmpty(newObject?.advisorPhoto) ||
          typeof newObject?.advisorPhoto === "string"
        ) {
          delete newObject.advisorPhoto;
        } else {
          newObject = { ...newObject, advisorPhoto: values.advisorPhoto[0] };
        }
        if (
          isEmpty(newObject?.quoteOwnerUrl) ||
          typeof newObject?.quoteOwnerUrl === "string"
        ) {
          delete newObject.quoteOwnerUrl;
        } else {
          newObject = { ...newObject, quoteOwnerUrl: values.quoteOwnerUrl[0] };
        }

        updateInvestorProfile(
          handleFreshReturnFormData({
            isEditForm: false,
            obj: {
              ...newObject,
            },
          }),
        );
      })}
      id="investor-setting-form"
    >
      <Grid2 container spacing={2}>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Micro-website Domain
          </Typography>
        </Grid2>

        <Grid2
          container
          size={9}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={6}>
            <Controller
              name="domainName"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter a valid domain name",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKTextField
                    {...field}
                    inputLabel="Domain Name"
                    requiredField
                    sx={{ borderColor: "green" }}
                    onChange={(event) => {
                      field.onChange(
                        event.target.value.toLowerCase().replace(/\s/g, ""),
                      );
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end" sx={{ fontSize: 14 }}>
                            .gridkey.in
                          </InputAdornment>
                        ),
                      },
                    }}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2
            size={6}
            mt={"18px"}
            display={"flex"}
            alignItems={"center"}
            gap={2}
          >
            <GKButton
              variant="contained"
              loading={isLoading}
              onClick={() => confirmDomainName(form.getValues("domainName"))}
            >
              Confirm domain
            </GKButton>
            {form.getValues("isVerified") ? (
              <Typography
                display={"flex"}
                alignItems={"center"}
                gap={1}
                color={"var(--success-700)"}
                border={`1px solid var(--success-700)`}
                p={1}
                borderRadius={2}
              >
                <VerifySolidIcon width={20} height={20} /> Verified
              </Typography>
            ) : (
              <Typography
                display={"flex"}
                alignItems={"center"}
                gap={1}
                color={`var(--warning-700)`}
                border={`1px solid var(--warning-700)`}
                p={1}
                borderRadius={2}
              >
                <HelpIcon /> Not Verified
              </Typography>
            )}
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Brand Guidelines
          </Typography>
        </Grid2>

        <Grid2
          container
          size={9}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={12}>
            <Controller
              name="companyLogo"
              control={form.control}
              render={() => (
                <GKImageUploader
                  croppedImage={croppedImage}
                  handleProfilePicDelete={() => {
                    handlePhotoDelete("companyLogo");
                  }}
                  selectedImage={selectedImage}
                  setCroppedImage={setCroppedImage}
                  setSelectedImage={setSelectedImage}
                  form={form}
                  logoName={"companyLogo"}
                  buttonName="Upload Company Logo"
                />
              )}
            />
          </Grid2>
          <Grid2 size={12} container gap={2}>
            <Grid2 size={2.5}>
              <Controller
                control={form.control}
                name="colorPalette.primary"
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"Primary Color"}
                    type="color"
                    style={{ borderRadius: "100%" }}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={2.5}>
              <Controller
                control={form.control}
                name="colorPalette.secondary"
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    inputLabel={"Secondary Color"}
                    type="color"
                    style={{ borderRadius: "100%" }}
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={12} my={1}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Micro-website Profile
          </Typography>
        </Grid2>
        <Grid2
          container
          size={9}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={12}>
            <Controller
              name="companyMetaName"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter company name",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKEditor
                    {...field}
                    inputLabel="Company Meta Name"
                    requiredField
                    error={!!error}
                    tooltipTitle="This is the official name that will appear on your micro-website and keep it consistent with your brand."
                    helperText={error?.message}
                    minHeight={50}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "fontColor",
                      "fontBackgroundColor",
                    ]}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="tagLine"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter tag line",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKEditor
                    {...field}
                    inputLabel="Tagline"
                    requiredField
                    tooltipTitle={`A short, catchy phrase that represents your brand's promise or core value. Displayed prominently on your profile.`}
                    error={!!error}
                    helperText={error?.message}
                    minHeight={50}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "heading",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "|",
                      "fontColor",
                      "fontBackgroundColor",
                      "|",
                      "alignment",
                    ]}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="tagLineBody"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter tag line description",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKEditor
                    {...field}
                    inputLabel="Tagline Body"
                    requiredField
                    error={!!error}
                    helperText={error?.message}
                    tooltipTitle={`Add a brief description that supports your tagline—highlight your strengths, philosophy, or what sets your firm apart.`}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "heading",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "|",
                      "fontColor",
                      "fontBackgroundColor",
                      "|",
                      "alignment",
                      "link",
                      "|",
                      "bulletedList",
                      "numberedList",
                    ]}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>
        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Quotes
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Grid2 size={12}>
              <GKImageUploader
                croppedImage={croppedQuoteImage}
                handleProfilePicDelete={() => {
                  handlePhotoDelete("quoteOwnerUrl");
                }}
                selectedImage={selectedQuoteImage}
                setCroppedImage={setCroppedQuoteImage}
                setSelectedImage={setSelectedQuoteImage}
                form={form}
                logoName={"quoteOwnerUrl"}
                buttonName="Upload Quote Owner Photo"
              />
            </Grid2>
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="quoteOwnerName"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Owner name"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="quoteOwnerDesignation"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Owner Designation"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="quotes"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextArea
                  {...field}
                  requiredField
                  inputLabel="Quote"
                  rows={4}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Advisor Details
          </Typography>
        </Grid2>
        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="advisorPhoto"
              control={form.control}
              render={() => (
                <GKImageUploader
                  croppedImage={croppedAdvisorImage}
                  handleProfilePicDelete={() => {
                    handlePhotoDelete("advisorPhoto");
                  }}
                  selectedImage={selectedAdvisorImage}
                  setCroppedImage={setCroppedAdvisorImage}
                  setSelectedImage={setSelectedAdvisorImage}
                  form={form}
                  logoName={"advisorPhoto"}
                  buttonName="Upload Advisor Photo"
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="advisorName"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter advisor name",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Advisor name"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="sebiRegistered"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter SEBI Registered number",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="SEBI Registered No."
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="yearsOfExperience"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter years of experience",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Years of Experience"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="advisorInfo"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter description",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => (
                <GKEditor
                  {...field}
                  requiredField
                  inputLabel="Advisor Introduction"
                  error={!!error}
                  helperText={error?.message}
                  toolbar={[
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    "|",
                    "fontColor",
                    "fontBackgroundColor",
                    "|",
                    "alignment",
                    "link",
                    "|",
                    "bulletedList",
                    "numberedList",
                  ]}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="advisorEmail"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter email",
                },
                pattern: {
                  value: emailRegex,
                  message: "Please enter a valid email",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Email"
                  requiredField
                  error={!!error}
                  helperText={!!error && error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={4}>
            <Controller
              name="advisorPhone"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Phone Number"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="advisorAddress"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter address",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextArea
                  {...field}
                  requiredField
                  inputLabel="Address"
                  rows={4}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="youtubeThumbnail"
              control={form.control}
              render={() => (
                <GKImageUploader
                  croppedImage={croppedYoutubeThumbnail}
                  selectedImage={selectedYoutubeThumbnail}
                  setCroppedImage={setCroppedYoutubeThumbnail}
                  setSelectedImage={setSelectedYoutubeThumbnail}
                  form={form}
                  logoName={"youtubeThumbnail"}
                  buttonName="Upload Youtube Thumbnail"
                  handleProfilePicDelete={() => {
                    handlePhotoDelete("youtubeThumbnail");
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="youtubeLink"
              control={form.control}
              render={({ field }) => (
                <GKTextField {...field} inputLabel="Youtube Link" />
              )}
            />
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Manager Disclosure
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="managerDisclosure"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter Disclosure",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKEditor
                    {...field}
                    inputLabel="Disclosure"
                    requiredField
                    error={!!error}
                    helperText={error?.message}
                    minHeight={50}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "fontColor",
                      "fontBackgroundColor",
                    ]}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={3}>
          <Typography
            fontSize={16}
            fontWeight={500}
            color={"var(--neutral-500)"}
          >
            Footer Content
          </Typography>
        </Grid2>

        <Grid2 container size={9} spacing={2}>
          <Grid2 size={12}>
            <Controller
              name="footerTagLine"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter company name",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKEditor
                    {...field}
                    inputLabel="Footer Tagline"
                    requiredField
                    error={!!error}
                    helperText={error?.message}
                    minHeight={50}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "fontColor",
                      "fontBackgroundColor",
                    ]}
                  />
                );
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <Controller
              name="footerTagLineBody"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please enter company name",
                },
                validate: validateNotWhitespaceOnly,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <GKEditor
                    {...field}
                    inputLabel="Footer Body"
                    requiredField
                    error={!!error}
                    helperText={error?.message}
                    minHeight={50}
                    toolbar={[
                      "undo",
                      "redo",
                      "|",
                      "fontColor",
                      "fontBackgroundColor",
                    ]}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>

        <Grid2
          size={12}
          mt={2}
          p={1}
          gap={1}
          display={"flex"}
          justifyContent={"flex-end"}
          position={"sticky"}
          bgcolor={"var(--white)"}
          bottom={-7}
        >
          <GKButton
            type="submit"
            variant="outlined"
            loading={updateInvestorLoading}
          >
            Save
          </GKButton>
          <GKButton
            variant="contained"
            onClick={() => {
              setInvestorSettingsConfirmation(true);
            }}
            loading={updateInvestorLoading}
          >
            Publish
          </GKButton>
        </Grid2>
      </Grid2>
      {investorSettingsConfirmation && (
        <InvestorSettingsConfirmation
          open={investorSettingsConfirmation}
          setOpen={setInvestorSettingsConfirmation}
          investorData={data?.data}
        />
      )}
    </form>
  );
};

export default WebsiteSettings;
